import axios from 'axios'
import { recaptchaSiteKey, auth } from '@/config/api'

// reCAPTCHA configuration
const RECAPTCHA_SITE_KEY = recaptchaSiteKey
const AUTH_WORKER_URL = auth

// Create axios instance for auth worker
const authClient = axios.create({
  baseURL: AUTH_WORKER_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
})

// Load Google reCAPTCHA script for invisible reCAPTCHA
export function loadRecaptchaScript() {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.grecaptcha) {
      resolve(window.grecaptcha)
      return
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="recaptcha"]')) {
      // Wait for it to load
      const checkLoaded = () => {
        if (window.grecaptcha) {
          resolve(window.grecaptcha)
        } else {
          setTimeout(checkLoaded, 100)
        }
      }
      checkLoaded()
      return
    }

    // Load the script for invisible reCAPTCHA
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=' + RECAPTCHA_SITE_KEY
    script.async = true
    script.defer = true

    script.onload = () => {
      // Wait for grecaptcha to be available
      const checkReady = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            resolve(window.grecaptcha)
          })
        } else {
          setTimeout(checkReady, 100)
        }
      }
      checkReady()
    }

    script.onerror = () => {
      reject(new Error('Failed to load reCAPTCHA script'))
    }

    document.head.appendChild(script)
  })
}

// Execute invisible reCAPTCHA
export function executeRecaptcha(action = 'email_check') {
  return new Promise((resolve, reject) => {
    loadRecaptchaScript()
      .then((grecaptcha) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
            .then(resolve)
            .catch(reject)
        })
      })
      .catch(reject)
  })
}

// Verify reCAPTCHA token with backend
export async function verifyRecaptchaToken(token, email = null) {
  try {
    const response = await authClient.post('/recaptcha/verify', {
      token,
      email
    })
    return response.data
  } catch (error) {
    console.error('reCAPTCHA verification error:', error)
    throw new Error(error.response?.data?.error || 'Failed to verify reCAPTCHA')
  }
}

// Execute reCAPTCHA and verify token in one step
export async function executeAndVerifyRecaptcha(action = 'email_check', email = null) {
  try {
    // Execute invisible reCAPTCHA
    const token = await executeRecaptcha(action)

    // Verify token with backend
    const response = await verifyRecaptchaToken(token, email)

    return response
  } catch (error) {
    console.error('reCAPTCHA execution and verification error:', error)
    throw new Error(error.message || 'Failed to complete reCAPTCHA verification')
  }
}


